// Copyright (c) 2025 WiseTime. All rights reserved.

import { EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { equals } from "ramda"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { t } from "@lingui/macro"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { showSnackBar } from "../../snackbar/actions"
import type { RootState } from "../../../app/store"
import {
  GetTrainingNarrativeRequestSchema,
  TrainingNarrative,
} from "../../../generated/wisetime/user/assistant/v4/preferences_pb"
import { executor, Init, narratePreferencesService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"

type GetTrainingNarrativeRpc = (request: Init<typeof GetTrainingNarrativeRequestSchema>) => Observable<TrainingNarrative>

export const getTrainingNarrative = (getTrainingNarrativeRpc: GetTrainingNarrativeRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.getTrainingNarrative.match),
      mergeMap(({ payload }) => getTrainingNarrativeRpc(payload).pipe(
        withLatestFrom(state$),
        mergeMap(([trainingNarrative, state]) => {
          if (state.narratePreferences.previewNarratePreferences !== undefined
            && !equals(payload.preferences, state.narratePreferences.previewNarratePreferences)
          ) {
            return EMPTY
          }
          return of(Slice.setTrainingNarrative(trainingNarrative))
        }),
        catchError(_ => of(showSnackBar({
          message: t({
            id: "msg_get_training_narrative_failed",
            message: "Failed to load narrative. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
          }),
          messageType: "ERROR",
        }))),
      )),
    )

const getTrainingNarrativeRpc = (request: Init<typeof GetTrainingNarrativeRequestSchema>): Observable<TrainingNarrative> =>
  executor(() => narratePreferencesService.getTrainingNarrative(create(GetTrainingNarrativeRequestSchema, request)))

export default getTrainingNarrative(getTrainingNarrativeRpc)
